// The screen shown when a guarded route cannot be entered because the session could not be
// READ — a 5xx or an offline tenant, as distinct from being signed out.
//
// In its own module because BOTH layouts need it. It lived inside `AuthLayout` while that was
// the only guarded branch; `ExtensionLayout` then rendered nothing at all for `unavailable`,
// so a frame whose tenant was down showed chrome above an unexplained blank — which is the
// exact failure this component was written to prevent, reintroduced by a second caller not
// having it.
import type { ReactElement } from 'react'
import { LoadingButton, PageHost } from '@woo-ext/ui'

/**
 * Shown when a guarded route cannot be entered because the session could not be READ — a
 * 5xx or an offline tenant, as distinct from being signed out.
 *
 * It exists because failing closed without saying so is a blank frame: the user sees the
 * header and nothing else, forever, with no way to retry. Upstream never reaches this state
 * (ui-router resolves before entering), so there is no markup to port.
 *
 * ⚠️ IT STILL NEEDS A `PageHost`, and an earlier version wrongly assumed it did not. It
 * rendered a bare `<div class="page-content">` with the comment that this "reuses the
 * classes the other auth screens use so it is not unstyled" — but EVERY `.page-content`
 * rule in `packages/styles` is nested inside a `chrome-extension-*-page` host
 * (`_status.page.scss:21`, and the same at line 21 or 22 of ten more). Outside one, both
 * classes match nothing and this rendered as raw HTML: unpositioned text at the default
 * font, in the one state a user only sees when something is already broken.
 *
 * It borrows the STATUS page's host because that is what this is — a status message — and
 * its shape is copied from that page's own "Update required" branch: `.text-content` heading,
 * `.smaller-font` detail, `form-btn` action. Nothing else in `_status.page.scss` reaches
 * markup we do not render, so borrowing the host borrows no styling we did not ask for.
 */
export function SessionUnavailable({ onRetry }: { onRetry: () => void }): ReactElement {
  return (
    <PageHost name="chrome-extension-status-page" className="popup-styles" hostClassName="non-extended-ui">
      <div className="page-content">
        <div className="text-content">
          Connection problem
          <br />
          <br />
          <div className="smaller-font">We could not reach Woo to check your session.</div>
        </div>
        <LoadingButton text="Try again" classOverride="form-btn" tabIndex={1} autoFocus onClick={onRetry} />
      </div>
    </PageHost>
  )
}
